import {
  buildDefaultReferenceListEntries,
  mergeReferenceListEntries,
  resolveReferenceListDefaultId,
  resolveReferenceListLabel,
} from "./reference-list-entries.js";

function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {{
 *   listKey: string,
 *   builtinIds: readonly string[],
 *   labelKeyForId: (id: string) => string,
 *   dbCodeById?: Record<string, string>,
 *   customDbFallback?: string,
 *   customIdPrefix?: string,
 * }} config
 */
export function createReferenceListModule(config) {
  const {
    listKey,
    builtinIds,
    labelKeyForId,
    dbCodeById = null,
    customDbFallback = "other",
    customDbStoresId = false,
    customIdPrefix = "custom_",
  } = config;

  const builtinSet = new Set(builtinIds);

  function defaults() {
    return buildDefaultReferenceListEntries(builtinIds);
  }

  function resolveEntries(lists) {
    const configured = lists && typeof lists === "object" ? lists[listKey] : null;
    return mergeReferenceListEntries(configured, defaults());
  }

  function resolveEntriesForAdmin(lists) {
    const configured = lists && typeof lists === "object" ? lists[listKey] : null;
    return mergeReferenceListEntries(configured, defaults(), { adminView: true });
  }

  function resolveLabel(entry, t) {
    return resolveReferenceListLabel(entry, t, labelKeyForId);
  }

  function resolveLabelById(id, lists, t) {
    const normalized = asStr(id);
    if (!normalized) return "";
    const entry = resolveEntries(lists).find((row) => row.id === normalized);
    if (entry) return resolveLabel(entry, t);
    const key = labelKeyForId(normalized);
    if (key && typeof t === "function") {
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return normalized;
  }

  function resolveIds(lists) {
    return resolveEntries(lists).map((row) => row.id);
  }

  function resolveDefaultId(lists) {
    return resolveReferenceListDefaultId(resolveEntriesForAdmin(lists));
  }

  function isAllowedId(id, lists) {
    const normalized = asStr(id);
    if (!normalized) return false;
    return resolveIds(lists).includes(normalized);
  }

  function mapToDbCode(uiId) {
    const id = asStr(uiId);
    if (!id) return "";
    if (dbCodeById && dbCodeById[id]) return dbCodeById[id];
    if (builtinSet.has(id)) return id;
    if (id.startsWith(customIdPrefix) || id.includes("_custom_")) {
      if (customDbStoresId) return id;
      return customDbFallback || "other";
    }
    return "";
  }

  return {
    listKey,
    builtinIds,
    defaults,
    resolveEntries,
    resolveEntriesForAdmin,
    resolveLabel,
    resolveLabelById,
    resolveIds,
    resolveDefaultId,
    isAllowedId,
    mapToDbCode,
  };
}
